// ** I18n Imports

import axios from "axios"
import i18n from 'i18next'
import Backend from 'i18next-xhr-backend'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import TimeAgo from 'javascript-time-ago'

import pl from 'javascript-time-ago/locale/pl.json'
import en from 'javascript-time-ago/locale/en.json'
import sr from 'javascript-time-ago/locale/sr-Latn.json'
import es from 'javascript-time-ago/locale/es.json'
import pt from 'javascript-time-ago/locale/pt.json'
import ru from 'javascript-time-ago/locale/ru.json'
import de from 'javascript-time-ago/locale/de.json'
import fr from 'javascript-time-ago/locale/fr.json'

import { isUserLoggedIn, getUserData } from '@utils'
import { getCachedRoutePath } from '../../router/routes/routeKeys'

let lang = window.localStorage.getItem('i18nextLng') ?? undefined

TimeAgo.addLocale(pl)
TimeAgo.addLocale(en)
TimeAgo.addLocale(sr)
TimeAgo.addLocale(es)
TimeAgo.addLocale(pt)
TimeAgo.addLocale(de)
TimeAgo.addLocale(fr)
TimeAgo.addLocale(ru)


if (isUserLoggedIn() !== null) {
  const user = getUserData().user
  lang = user.lang.code
  if (lang === 'sr') {
    TimeAgo.setDefaultLocale('sr-Latn')
  } else {
    TimeAgo.setDefaultLocale(lang)
  }
} else {
  if (lang === 'sr') {
    TimeAgo.setDefaultLocale('sr-Latn')
  }
}

// Get initial language synchronously
function getInitialLanguage() {
  const defaultLang = 'en'
  
  // Check if user is logged in first (synchronous check)
  if (isUserLoggedIn()) {
    try {
      const user = getUserData().user
      if (user && user.lang && user.lang.code) {
        return user.lang.code
      }
    } catch (error) {
      // Fall through to other checks
    }
  }
  
  // Check localStorage for cached language
  const storedLang = window.localStorage.getItem('i18nextLng')
  if (storedLang) {
    return storedLang.split('-')[0] // Remove locale suffix if present
  }
  
  // Check for cached detected language
  const cachedLang = window.localStorage.getItem('detectedLang')
  if (cachedLang) {
    return cachedLang
  }
  
  return defaultLang
}

async function detectLanguage() {
  const defaultLang = 'en'
  let lang = defaultLang

  if (isUserLoggedIn()) {
    try {
      const user = getUserData().user
      lang = user.lang.code || defaultLang
      return lang
    } catch (error) {
      // Fall through
    }
  }

  const cachedLang = window.localStorage.getItem('detectedLang')
  const lastCheck = window.localStorage.getItem('lastLangCheck')
  const now = Date.now()
  const sixHours = 6 * 60 * 60 * 1000

  if (cachedLang && lastCheck && now - lastCheck < sixHours) {
    return cachedLang
  }

  try {
    const response = await axios.get('https://ipapi.co/json/')
    const countryCode = response.data.country_code

    if (countryCode === 'RS') {
      lang = 'sr'
    } else if (countryCode === 'PL') {
      lang = 'pl'
    }  else if (countryCode === 'ES') {
      lang = 'es'
    } else if (countryCode === 'PT') {
      lang = 'pt'
    } else if (countryCode === 'BR') {
      lang = 'pt'
    } else if (countryCode === 'FR') {
      lang = 'fr'
    } else if (countryCode === 'RU') {
      lang = 'ru'
    } else if (countryCode === 'DE') {
      lang = 'de'
    } else {
      lang = defaultLang
    }

    window.localStorage.setItem('detectedLang', lang)
    window.localStorage.setItem('lastLangCheck', now.toString())
  } catch (error) {
    lang = cachedLang || defaultLang
  }

  return lang
}

// Initialize i18next synchronously with initial language
const initialLang = getInitialLanguage()

i18n
  // Enables the i18next backend
  .use(Backend)
  // Enable automatic language detection
  .use(LanguageDetector)
  // Enables the hook initialization module
  .use(initReactI18next)
  .init({
    lng: initialLang,
    detection: {
      order: ['navigator', 'localStorage', 'cookie', 'htmlTag'],
      caches: ['localStorage']
    },
    backend: {
      loadPath: '/assets/data/locales/{{lng}}.json'
    },
    fallbackLng: {
      sr: ['sr'],
      'sr-Latn': ['sr'],
      'en-US': ['en'],
      'es-ES': ['es'],
      'pl-PL': ['pl'],
      'pt-PT': ['pt'],
      'pt-BR': ['pt'],
      'de-DE': ['de'],
      'ru-RU': ['ru'],
      'fr-FR': ['fr'],
      default: ['en']
    },
    debug: false,
    keySeparator: false,
    react: {
      useSuspense: false // Changed to false to avoid blocking
    },
    interpolation: {
      escapeValue: false,
      formatSeparator: ','
    }
  })

// Update language asynchronously once detection completes (if different)
;(async () => {
  try {
    const detectedLang = await detectLanguage()
    if (detectedLang !== initialLang && i18n.language !== detectedLang) {
      await i18n.changeLanguage(detectedLang)
    }
  } catch (error) {
    console.error('Error detecting language:', error)
  }
})()

export default i18n

export function trans(key, opts) {
  const value = i18n.t(key, {
    ...opts,
    postProcess: `reactPostprocessor`
  })

  // Hardening for route keys: if translation is not ready yet or missing,
  // fall back to route mapping and normalize with leading slash
  if (typeof key === 'string' && key.startsWith('route_link_')) {
    if (!value || value === key) {
      const lang = (i18n.language || 'en').split('-')[0]
      return getCachedRoutePath(key, lang)
    }
    return typeof value === 'string' && value.startsWith('/') ? value : `/${value}`
  }

  return value
}